import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, ArrowLeft, Search, Sparkles } from "lucide-react";

const B2BRegister = () => {
  const nav = useNavigate();
  const [f, setF] = useState({
    nom_entreprise: "", email: "", password: "", password2: "",
    contact_nom: "", telephone: "", tva: "", adresse: "", ville: "", code_postal: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [country, setCountry] = useState("FR");            // FR ou BE pour recherche par nom
  const [suggestions, setSuggestions] = useState([]);
  const [lookingUp, setLookingUp] = useState(false);

  const applyCompany = (c) => {
    setF((prev) => ({
      ...prev,
      nom_entreprise: c.nom || prev.nom_entreprise,
      tva: c.tva || prev.tva,
      adresse: c.adresse || prev.adresse,
      ville: c.ville || prev.ville,
      code_postal: c.code_postal || prev.code_postal,
    }));
    setSuggestions([]);
    toast.success(`${c.nom} — ${c.adresse}, ${c.code_postal} ${c.ville}`);
  };

  const lookupByName = async (q, ctry) => {
    setF({ ...f, nom_entreprise: q });
    if (!q || q.length < 3) { setSuggestions([]); return; }
    setLookingUp(true);
    try {
      const r = await axios.get(`${API}/company-lookup?q=${encodeURIComponent(q)}&country=${ctry}`);
      setSuggestions(r.data.results || []);
    } catch { /* silent */ }
    finally { setLookingUp(false); }
  };

  const lookupVAT = async () => {
    if (!f.tva || f.tva.length < 4) { toast.error("Saisis un numéro TVA (BE0XXXXXXXXX)"); return; }
    setLookingUp(true);
    try {
      const r = await axios.get(`${API}/company-lookup/vat?vat=${encodeURIComponent(f.tva)}`);
      applyCompany(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "TVA non trouvée");
    } finally { setLookingUp(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.password.length < 6) { setErr("Mot de passe : min 6 caractères"); return; }
    if (f.password !== f.password2) { setErr("Les mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      const { password2: _p2, ...payload } = f;
      await axios.post(`${API}/public/b2b/register`, payload);
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-3">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-3xl">✓</div>
            <h2 className="text-xl font-bold text-slate-800">Demande envoyée</h2>
            <p className="text-slate-600 text-sm">Votre demande d&apos;accès a été enregistrée. Notre équipe vérifiera votre compte et vous recevrez un email de confirmation une fois activé.</p>
            <Button onClick={() => nav("/b2b-login")} className="bg-indigo-600 hover:bg-indigo-700" data-testid="b2b-register-back">Retour à la connexion</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-slate-50 p-4">
      <Card className="max-w-lg w-full shadow-lg">
        <CardContent className="pt-6 space-y-4">
          <div className="text-center space-y-2">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-indigo-100 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-indigo-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Demande d&apos;accès B2B</h1>
            <p className="text-sm text-slate-500">Créez votre compte — validation par TopCocoon sous 24h</p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            {/* Autocomplétion société */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                <p className="text-xs font-semibold text-indigo-700">Recherche automatique par nom</p>
                <div className="ml-auto flex gap-1">
                  <button type="button" onClick={() => { setCountry("BE"); setSuggestions([]); }} className={`text-[10px] px-2 py-0.5 rounded-full ${country === "BE" ? "bg-indigo-600 text-white" : "bg-white border"}`} data-testid="b2b-reg-country-be">🇧🇪 BE</button>
                  <button type="button" onClick={() => { setCountry("FR"); setSuggestions([]); }} className={`text-[10px] px-2 py-0.5 rounded-full ${country === "FR" ? "bg-indigo-600 text-white" : "bg-white border"}`} data-testid="b2b-reg-country-fr">🇫🇷 FR</button>
                </div>
              </div>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder={country === "BE" ? "Nom ou n° TVA (BE0...) …" : "Tape le nom de ton entreprise…"}
                  value={f.nom_entreprise}
                  onChange={(e) => lookupByName(e.target.value, country)}
                  className="pl-8"
                  data-testid="b2b-reg-search"
                />
                {suggestions.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {suggestions.map((s, i) => (
                      <button
                        key={s.enterprise_number || s.siret || i}
                        type="button"
                        onClick={() => applyCompany(s)}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-xs border-b last:border-0"
                        data-testid={`b2b-reg-suggest-${i}`}
                      >
                        <div className="font-semibold text-slate-700">{s.nom}{s.nom_commercial && s.nom_commercial !== s.nom && <span className="text-slate-400"> · {s.nom_commercial}</span>}</div>
                        <div className="text-slate-400">{s.tva} · {s.adresse}, {s.code_postal} {s.ville}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {lookingUp && <p className="text-[10px] text-slate-400">Recherche…</p>}
              <p className="text-[10px] text-slate-500">
                {country === "BE"
                  ? "🇧🇪 Base KBO officielle (2M+ entreprises, adresses à jour mensuel)"
                  : "🇫🇷 recherche-entreprises.api.gouv.fr"}
              </p>
            </div>
            <div>
              <Label>Nom de l&apos;entreprise *</Label>
              <Input required value={f.nom_entreprise} onChange={(e) => setF({ ...f, nom_entreprise: e.target.value })} data-testid="b2b-reg-nom-ent" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Email *</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="b2b-reg-email" /></div>
              <div><Label>Téléphone</Label><Input value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} data-testid="b2b-reg-tel" /></div>
              <div><Label>Mot de passe *</Label><Input type="password" required minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="b2b-reg-pwd" /></div>
              <div><Label>Confirmer *</Label><Input type="password" required value={f.password2} onChange={(e) => setF({ ...f, password2: e.target.value })} data-testid="b2b-reg-pwd2" /></div>
              <div><Label>Nom du contact</Label><Input value={f.contact_nom} onChange={(e) => setF({ ...f, contact_nom: e.target.value })} /></div>
              <div><Label>N° TVA</Label><Input value={f.tva} onChange={(e) => setF({ ...f, tva: e.target.value })} placeholder="BE0123.456.789" /></div>
              <div className="sm:col-span-2"><Label>Adresse</Label><Input value={f.adresse} onChange={(e) => setF({ ...f, adresse: e.target.value })} /></div>
              <div><Label>CP</Label><Input value={f.code_postal} onChange={(e) => setF({ ...f, code_postal: e.target.value })} /></div>
              <div><Label>Ville</Label><Input value={f.ville} onChange={(e) => setF({ ...f, ville: e.target.value })} /></div>
            </div>
            {err && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="b2b-reg-error">{err}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" data-testid="b2b-reg-submit">
              {loading ? "Envoi…" : "Créer mon compte"}
            </Button>
          </form>
          <Link to="/b2b-login" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700" data-testid="b2b-reg-back">
            <ArrowLeft className="w-3 h-3" /> Retour à la connexion
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default B2BRegister;
